import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { AnnotatorAccessDto } from 'types/api.types';
import {
  assignAnnotator,
  getAnnotatorAccessList,
  removeAnnotatorAccess,
  updateAnnotatorAccess,
} from 'services/annotatorsAdminService';

type ToastSeverity = 'success' | 'error';

const AnnotatorAccess: React.FC = () => {
  const [annotators, setAnnotators] = useState<AnnotatorAccessDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [annotatorEmail, setAnnotatorEmail] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [isUpdatingByEmail, setIsUpdatingByEmail] = useState<Record<string, boolean>>({});
  const [isRemovingByEmail, setIsRemovingByEmail] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<{ message: string; severity: ToastSeverity } | null>(null);

  const showErrorToast = (message: string) => setToast({ message, severity: 'error' });
  const showSuccessToast = (message: string) => setToast({ message, severity: 'success' });

  const sortedAnnotators = useMemo(
    () => [...annotators].sort((a, b) => a.annotatorEmail.localeCompare(b.annotatorEmail)),
    [annotators],
  );

  const loadData = async () => {
    setIsLoading(true);
    setErrorMessage(null);

    const result = await getAnnotatorAccessList();
    if (!result) {
      setErrorMessage('Unable to load annotator access list.');
      setIsLoading(false);
      return;
    }

    setAnnotators(result.annotators);
    setIsLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleAssign = async () => {
    const email = annotatorEmail.trim().toLowerCase();
    if (!email) {
      showErrorToast('Annotator email is required.');
      return;
    }

    setIsAssigning(true);
    const created = await assignAnnotator(email);
    setIsAssigning(false);

    if (!created) {
      showErrorToast('Unable to grant annotator access.');
      return;
    }

    setAnnotators((current) => {
      const withoutExisting = current.filter(
        (row) => row.annotatorEmail !== created.annotatorEmail,
      );
      return [...withoutExisting, created];
    });
    setAnnotatorEmail('');
    showSuccessToast('Annotator access granted.');
  };

  const handleToggleActive = async (row: AnnotatorAccessDto) => {
    setIsUpdatingByEmail((current) => ({ ...current, [row.annotatorEmail]: true }));
    const updated = await updateAnnotatorAccess(row.annotatorEmail, !row.isActive);
    setIsUpdatingByEmail((current) => ({ ...current, [row.annotatorEmail]: false }));

    if (!updated) {
      showErrorToast('Unable to update annotator access.');
      return;
    }

    setAnnotators((current) =>
      current.map((annotator) =>
        annotator.annotatorEmail === updated.annotatorEmail ? updated : annotator,
      ),
    );
    showSuccessToast(updated.isActive ? 'Annotator access activated.' : 'Annotator access deactivated.');
  };

  const handleRemove = async (row: AnnotatorAccessDto) => {
    setIsRemovingByEmail((current) => ({ ...current, [row.annotatorEmail]: true }));
    const result = await removeAnnotatorAccess(row.annotatorEmail);
    setIsRemovingByEmail((current) => ({ ...current, [row.annotatorEmail]: false }));

    if (!result.success) {
      showErrorToast(result.errorMessage ?? 'Unable to remove annotator access.');
      return;
    }

    setAnnotators((current) =>
      current.filter((annotator) => annotator.annotatorEmail !== row.annotatorEmail),
    );
    showSuccessToast('Annotator access removed.');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h5" component="h1">
        Annotator access
      </Typography>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Grant annotator access
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <TextField
            label="Annotator email"
            value={annotatorEmail}
            onChange={(event) => setAnnotatorEmail(event.target.value)}
            fullWidth
          />
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={isAssigning}
            sx={{ minWidth: 140 }}
          >
            {isAssigning ? 'Granting...' : 'Grant access'}
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Current annotators
        </Typography>
        {sortedAnnotators.length === 0 ? (
          <Typography color="text.secondary">No annotators configured yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAnnotators.map((row) => (
                <TableRow key={row.annotatorEmail}>
                  <TableCell>{row.annotatorEmail}</TableCell>
                  <TableCell>
                    <Switch
                      checked={row.isActive}
                      onChange={() => handleToggleActive(row)}
                      disabled={isUpdatingByEmail[row.annotatorEmail]}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      color="error"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => handleRemove(row)}
                      disabled={isRemovingByEmail[row.annotatorEmail]}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert onClose={() => setToast(null)} severity={toast.severity} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Stack>
  );
};

export default AnnotatorAccess;
